import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { ArrowRight, FileText, CheckCircle, AlertCircle, MapPin } from 'lucide-react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';

const LeasePlanRequirements = () => {
  return (
    <>
      <Helmet>
        <title>Lease Plan Requirements | Land Registry Guide | What You Need | Energy Performance Direct</title>
        <meta name="description" content="Complete guide to Land Registry lease plan requirements. What your lease plan must include, common rejection reasons, and how to ensure your plan is accepted first time." />
        <meta name="keywords" content="lease plan requirements, land registry lease plan, lease plan guide, demise plan requirements, lease plan specification, HM Land Registry" />
        <link rel="canonical" href="https://energyperformancedirect.co.uk/resources/lease-plan-requirements" />
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <Navbar />

        <article className="py-16">
          <div className="container mx-auto px-4">
            <div className="max-w-3xl mx-auto">
              <div className="mb-8">
                <Link to="/resources" className="text-green-600 hover:underline text-sm">← Back to Resources</Link>
              </div>
              
              <header className="mb-12">
                <span className="text-sm font-medium text-green-600 bg-green-50 px-3 py-1 rounded-full">Property</span>
                <h1 className="text-4xl font-bold text-gray-900 mt-4 mb-6">Lease Plan Requirements: Land Registry Guide</h1>
                <p className="text-xl text-gray-600">Everything you need to know about lease plan requirements for HM Land Registry - what must be included, common problems, and how to get it right first time.</p>
              </header>

              <section className="prose prose-lg max-w-none">
                <h2 className="text-2xl font-bold mt-8 mb-4">What is a Lease Plan?</h2>
                <p className="text-gray-700 mb-4">
                  A lease plan is a scaled drawing that shows the extent of property being leased. It clearly identifies the demised premises (the area being let) using coloured edging, dimensions and floor layouts. Lease plans are required by HM Land Registry for registering leases of 7 years or more.
                </p>

                <h2 className="text-2xl font-bold mt-8 mb-4">When Do You Need a Lease Plan?</h2>
                <ul className="space-y-2 text-gray-700 mb-6">
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> <strong>New leases over 7 years</strong> - mandatory for registration</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> <strong>Lease extensions</strong> - if extending to over 7 years</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> <strong>Lease variations</strong> - if boundaries are changing</li>
                  <li className="flex items-start gap-2"><CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0 mt-1" /> <strong>First registration</strong> - of existing long leases</li>
                </ul>

                <h2 className="text-2xl font-bold mt-8 mb-4">Essential Requirements</h2>
                <p className="text-gray-700 mb-4">A compliant lease plan must include:</p>
                
                <div className="grid gap-4 my-6">
                  <Card className="border-green-200">
                    <CardContent className="pt-6">
                      <div className="flex gap-4">
                        <MapPin className="h-6 w-6 text-green-600 flex-shrink-0" />
                        <div>
                          <h3 className="font-semibold mb-2">Location Plan</h3>
                          <p className="text-gray-600 text-sm">An Ordnance Survey based plan at 1:1250 or 1:2500 scale showing the property in its wider context. The site should be clearly identifiable.</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-green-200">
                    <CardContent className="pt-6">
                      <div className="flex gap-4">
                        <FileText className="h-6 w-6 text-green-600 flex-shrink-0" />
                        <div>
                          <h3 className="font-semibold mb-2">Floor Plan</h3>
                          <p className="text-gray-600 text-sm">A detailed floor plan at 1:100 or 1:200 scale showing the internal layout. For multi-storey leases, each floor should be shown.</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                  <Card className="border-green-200">
                    <CardContent className="pt-6">
                      <div className="flex gap-4">
                        <CheckCircle className="h-6 w-6 text-green-600 flex-shrink-0" />
                        <div>
                          <h3 className="font-semibold mb-2">Red Edging</h3>
                          <p className="text-gray-600 text-sm">The demised premises must be edged in red. This clearly shows exactly what is included in the lease. Other colours may indicate rights of way, parking, etc.</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                <h3 className="text-xl font-semibold mt-6 mb-3">Additional Requirements</h3>
                <ul className="space-y-2 text-gray-700 mb-6">
                  <li>• <strong>Scale bar or stated scale</strong> - so measurements can be verified</li>
                  <li>• <strong>North point</strong> - for orientation</li>
                  <li>• <strong>Property address</strong> - clearly stated</li>
                  <li>• <strong>Floor level</strong> - ground, first, basement, etc.</li>
                  <li>• <strong>Key/legend</strong> - if multiple colours used</li>
                </ul>

                <h2 className="text-2xl font-bold mt-8 mb-4">Common Rejection Reasons</h2>
                <div className="bg-red-50 border border-red-200 rounded-lg p-6 my-6">
                  <AlertCircle className="h-6 w-6 text-red-600 mb-3" />
                  <h3 className="font-semibold text-red-800 mb-3">Plans are often rejected for:</h3>
                  <ul className="space-y-2 text-red-700">
                    <li>• Demise not clearly edged in red</li>
                    <li>• Plan not to scale or scale not stated</li>
                    <li>• Location plan doesn't show sufficient context</li>
                    <li>• Floor level not identified</li>
                    <li>• Plan doesn't match lease description</li>
                    <li>• Poor quality scan or illegible text</li>
                    <li>• Insufficient detail to identify boundaries</li>
                  </ul>
                </div>

                <h2 className="text-2xl font-bold mt-8 mb-4">Format Requirements</h2>
                <p className="text-gray-700 mb-4">Land Registry accepts plans in various formats:</p>
                <ul className="space-y-2 text-gray-700 mb-6">
                  <li>• <strong>Paper</strong> - must be A4 or A3, printed clearly</li>
                  <li>• <strong>PDF</strong> - for electronic lodgement (preferred)</li>
                  <li>• <strong>JPEG/PNG</strong> - acceptable but PDF preferred</li>
                </ul>
                <p className="text-gray-700 mb-4">Plans should be clear, legible and capable of being reproduced. Colour printing is essential for showing the red edging.</p>

                <h2 className="text-2xl font-bold mt-8 mb-4">Related Services</h2>
                <div className="grid md:grid-cols-2 gap-4 my-6">
                  <Link to="/lease-plans" className="block">
                    <Card className="h-full hover:shadow-lg transition-shadow hover:border-green-300">
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">Lease Plans</h3>
                        <p className="text-gray-600 text-sm">Professional lease plans from £150</p>
                      </CardContent>
                    </Card>
                  </Link>
                  <Link to="/lease-plans-west-country" className="block">
                    <Card className="h-full hover:shadow-lg transition-shadow hover:border-green-300">
                      <CardContent className="pt-6">
                        <h3 className="font-semibold mb-2">West Country Lease Plans</h3>
                        <p className="text-gray-600 text-sm">Including measured surveys</p>
                      </CardContent>
                    </Card>
                  </Link>
                </div>
              </section>

              <div className="mt-12 p-6 bg-green-50 rounded-lg text-center">
                <h3 className="text-xl font-bold mb-3">Need a Lease Plan?</h3>
                <p className="text-gray-600 mb-4">Land Registry compliant plans, delivered fast.</p>
                <Link to="/booking">
                  <Button className="bg-green-600 hover:bg-green-700">
                    Order Lease Plan <ArrowRight className="ml-2 h-4 w-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </article>

        <Footer />
      </div>
    </>
  );
};

export default LeasePlanRequirements;
